/**
 * WeaveIn.ai Platform - Create Enrollment Page
 * Full-page form for creating new member enrollments in programs.
 * Replaces the former CreateEnrollmentModal.
 *
 * Pre-fill: reads `pendingEnrollmentMemberId` and `pendingEnrollmentProgramId`
 * from localStorage on mount, then clears them. Entry points (member profile,
 * programs page) set these keys before navigating to `activeTab="enrollment-new"`.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import useEnrollments from '@/hooks/useEnrollments';
import usePrograms from '@/hooks/usePrograms';
import useMembers from '@/hooks/useMembers';
import { EnrollmentCreate } from '@/services/api/enrollments';
import { config } from '@/src/config';
import NewCaseDetectionDialog from '@/components/episodes/NewCaseDetectionDialog';
import { EpisodeDetectionResult } from '@/services/api/caseEpisodes';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';

interface CreateEnrollmentPageProps {
  onBack: () => void;
  onSuccess?: (enrollmentId: number) => void;
}

export default function CreateEnrollmentPage({ onBack, onSuccess }: CreateEnrollmentPageProps) {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { createEnrollment, loading: enrollmentLoading } = useEnrollments({ autoFetch: false });
  const { programs } = usePrograms();
  const { members, loading: membersLoading, error: membersError } = useMembers({ page_size: 1000 });

  // Read pre-fill from localStorage once, then clear so a future visit
  // starts blank.
  const preselected = useMemo(() => {
    const memberRaw = localStorage.getItem('pendingEnrollmentMemberId');
    const programRaw = localStorage.getItem('pendingEnrollmentProgramId');
    localStorage.removeItem('pendingEnrollmentMemberId');
    localStorage.removeItem('pendingEnrollmentProgramId');
    return {
      memberId: memberRaw ? parseInt(memberRaw, 10) || 0 : 0,
      programId: programRaw ? parseInt(programRaw, 10) || 0 : 0,
    };
  }, []);

  const [formData, setFormData] = useState<EnrollmentCreate>({
    member_id: preselected.memberId,
    program_id: preselected.programId,
    status: 'pending',
    enrollment_date: new Date().toISOString().split('T')[0],
    start_date: '',
    end_date: '',
    assigned_case_manager_id: undefined,
    priority_level: 'medium',
    eligibility_verified: false,
    consent_obtained: false,
    notes: '',
    mcp: '',
  } as EnrollmentCreate & { mcp: string });

  const [error, setError] = useState<string | null>(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [staffUsers, setStaffUsers] = useState<any[]>([]);
  const [episodeDetection, setEpisodeDetection] = useState<EpisodeDetectionResult | null>(null);
  const [showDetectionDialog, setShowDetectionDialog] = useState(false);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');
        if (!cboId) return;
        const response = await fetch(`${config.weaveinApi.baseUrl}/api/cbos/${cboId}/users?staff_only=true&exclude_internal=true`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId,
          },
        });
        if (response.ok) {
          const all = await response.json();
          // Case Manager dropdown: DENYLIST, not allowlist. Include every org
          // role (incl. custom roles like "Disaster Case Manager"/dcm01,
          // partner_dcm) and only EXCLUDE platform/internal roles
          // (super_admin/ops_admin/platform_admin), portal members, and
          // Trust-WeaveIn internal email domains. An allowlist of role keys
          // silently dropped custom org roles — DCMs never appeared.
          const EXCLUDED_ROLES = ['super_admin', 'ops_admin', 'platform_admin', 'member'];
          const INTERNAL_DOMAINS = ['@trust.care', '@weavein.ai', '@payssurance.com', '@anthropic.com'];
          const filtered = (Array.isArray(all) ? all : []).filter((u: any) => {
            const role = (u.user_org_role || u.role || '').toLowerCase();
            if (EXCLUDED_ROLES.includes(role)) return false;
            const email = (u.email || '').toLowerCase();
            if (INTERNAL_DOMAINS.some(d => email.endsWith(d))) return false;
            return true;
          });
          setStaffUsers(filtered);
        }
      } catch (err) {
        console.error('Error fetching staff users:', err);
      }
    };
    fetchStaff();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.member_id || formData.member_id === 0) {
      setError(t('enrollments.select_member_error', 'Please select a member'));
      return;
    }

    if (!formData.program_id || formData.program_id === 0) {
      setError(t('enrollments.select_program_error', 'Please select a program'));
      return;
    }

    const selectedProgram = programs?.find((p) => p.id === formData.program_id);
    if (selectedProgram?.capacity && selectedProgram.current_enrollment >= selectedProgram.capacity) {
      if (!window.confirm(`This program is at capacity (${selectedProgram.current_enrollment}/${selectedProgram.capacity}). Enroll anyway?`)) {
        return;
      }
    }

    try {
      const enrollmentData = {
        member_id: formData.member_id,
        program_id: formData.program_id,
        enrollment_status: formData.status,
        enrollment_priority: formData.priority_level,
        enrollment_date: formData.enrollment_date,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        assigned_to: formData.assigned_case_manager_id || null,
        mcp: (formData as any).mcp?.trim() || null,
      };

      const created = await createEnrollment(enrollmentData as any);
      if (created) {
        const detection = (created as any).episode_detection as EpisodeDetectionResult | null;
        if (detection && detection.should_create_new && detection.action === 'PROMPT_USER') {
          setEpisodeDetection(detection);
          setShowDetectionDialog(true);
          onSuccess?.(created.id);
        } else {
          onSuccess?.(created.id);
          onBack();
        }
      }
    } catch (err: any) {
      console.error('Failed to create enrollment:', err);
      const errorDetail = err.response?.data?.detail || err.message || 'Unknown error';

      let userMessage = errorDetail;
      if (errorDetail.includes('not found or does not belong')) {
        userMessage = 'The selected member does not belong to your organization. Please contact support if you believe this is an error.';
      } else if (errorDetail.includes('already enrolled')) {
        userMessage = errorDetail;
      } else if (errorDetail.includes('Program ID')) {
        userMessage = 'The selected program is not accessible. Please try a different program.';
      } else if (err.response?.status === 404) {
        userMessage = 'Member or program not found. Please refresh the page and try again.';
      } else if (err.response?.status === 400) {
        userMessage = errorDetail;
      }

      setError(userMessage);
    }
  };

  const filteredMembers = members?.filter((m) => {
    if (!memberSearch) return true;
    const searchLower = memberSearch.toLowerCase();
    return (
      m.first_name?.toLowerCase().includes(searchLower) ||
      m.last_name?.toLowerCase().includes(searchLower) ||
      m.email?.toLowerCase().includes(searchLower) ||
      m.patient_no?.toLowerCase().includes(searchLower)
    );
  });

  const preselectedMember = preselected.memberId
    ? members?.find((m) => m.id === preselected.memberId)
    : null;
  const preselectedProgram = preselected.programId
    ? programs?.find((p) => p.id === preselected.programId)
    : null;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-6 flex items-center gap-3">
        <Button variant="secondary" size="sm" icon={<ArrowLeft className="w-4 h-4" />} onClick={onBack}>
          {t('common.back', 'Back')}
        </Button>
        {!tabbed && (
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          {t('enrollments.create_new_enrollment', 'Create New Enrollment')}
        </h1>
        )}
      </div>

      {error && (
        <Alert className="mb-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <AlertDescription className="text-red-800 dark:text-red-200">
            <strong>{t('enrollments.error', 'Error:')}</strong> {error}
          </AlertDescription>
        </Alert>
      )}

      {membersError && (
        <Alert className="mb-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
          <AlertDescription className="text-amber-800 dark:text-amber-200">
            <strong>{t('enrollments.warning', 'Warning:')}</strong> Failed to load members: {membersError}. Please refresh the page.
          </AlertDescription>
        </Alert>
      )}

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Member Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('enrollments.select_member', 'Select Member')} <span className="text-red-600">*</span>
            </label>
            {preselectedMember ? (
              <input
                type="text"
                value={`${preselectedMember.first_name || ''} ${preselectedMember.last_name || ''}`.trim() || `Member ${preselectedMember.id}`}
                disabled
                className="w-full rounded border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm"
              />
            ) : membersLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('enrollments.loading_members', 'Loading members...')}
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder={t('enrollments.search_members_by_name_email', 'Search members by name, email, or patient number...')}
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="mb-2 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  disabled={membersLoading || !!membersError}
                />
                <select
                  value={formData.member_id}
                  onChange={(e) => setFormData({ ...formData, member_id: parseInt(e.target.value) })}
                  required
                  size={Math.min(filteredMembers?.length || 1, 8)}
                  className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  disabled={membersLoading || !!membersError}
                >
                  <option value={0}>
                    {membersError
                      ? '-- Error loading members --'
                      : !members || members.length === 0
                      ? '-- No members available --'
                      : '-- Select a member --'}
                  </option>
                  {filteredMembers?.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                      {member.patient_no && ` (${member.patient_no})`}
                      {member.email && ` - ${member.email}`}
                    </option>
                  ))}
                </select>
                {!membersLoading && !membersError && filteredMembers && filteredMembers.length === 0 && memberSearch && (
                  <p className="mt-1 text-xs text-gray-500">No members found matching "{memberSearch}"</p>
                )}
                {!membersLoading && !membersError && (!members || members.length === 0) && !memberSearch && (
                  <p className="mt-1 text-xs text-amber-600">
                    {t('enrollments.no_members_found_in_your', 'No members found in your organization. Please add members first.')}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Program Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('enrollments.select_program', 'Select Program')} <span className="text-red-600">*</span>
            </label>
            <select
              value={formData.program_id}
              onChange={(e) => setFormData({ ...formData, program_id: parseInt(e.target.value) })}
              required
              disabled={!!preselectedProgram}
              className={`w-full rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm ${
                preselectedProgram ? 'bg-gray-100 dark:bg-gray-800' : 'bg-white dark:bg-gray-900'
              }`}
            >
              <option value={0}>-- Select a program --</option>
              {programs?.filter((p) => p.is_active).map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name} - {program.program_type}
                  {program.capacity && ` (${program.current_enrollment}/${program.capacity})`}
                  {program.capacity && program.current_enrollment >= program.capacity && ' - AT CAPACITY'}
                </option>
              ))}
            </select>
            {formData.program_id > 0 && (() => {
              const selectedProgram = programs?.find((p) => p.id === formData.program_id);
              return selectedProgram?.description ? (
                <p className="mt-1 text-xs text-gray-500">{selectedProgram.description}</p>
              ) : null;
            })()}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('enrollments.status', 'Status')}
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              >
                {/* Values must match the program_enrollments.enrollment_status
                    DB CHECK constraint exactly, else the POST 500s. */}
                <option value="pending">{t('enrollments.pending', 'Pending')}</option>
                <option value="waitlisted">{t('enrollments.waitlisted', 'Waitlisted')}</option>
                <option value="active">{t('enrollments.active', 'Active')}</option>
                <option value="suspended">{t('enrollments.suspended', 'Suspended')}</option>
                <option value="completed">{t('enrollments.completed', 'Completed')}</option>
                <option value="cancelled">{t('enrollments.cancelled', 'Cancelled')}</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('enrollments.priority_level', 'Priority Level')}
              </label>
              <select
                value={formData.priority_level}
                onChange={(e) => setFormData({ ...formData, priority_level: e.target.value })}
                className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              >
                <option value="low">{t('enrollments.low', 'Low')}</option>
                <option value="medium">{t('enrollments.medium', 'Medium')}</option>
                <option value="high">{t('enrollments.high', 'High')}</option>
                <option value="urgent">{t('enrollments.urgent', 'Urgent')}</option>
              </select>
            </div>
          </div>

          {/* Case Manager */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('enrollments.case_manager', 'Assign Case Manager')}
            </label>
            <select
              value={formData.assigned_case_manager_id || 0}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                setFormData({ ...formData, assigned_case_manager_id: val || undefined });
              }}
              className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            >
              <option value={0}>-- {t('enrollments.no_case_manager', 'No Case Manager')} --</option>
              {staffUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name || user.email}{user.role_names ? ` — ${user.role_names}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('enrollments.enrollment_date', 'Enrollment Date')}
              </label>
              <input
                type="date"
                value={formData.enrollment_date}
                onChange={(e) => setFormData({ ...formData, enrollment_date: e.target.value })}
                className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('enrollments.start_date', 'Start Date')}
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('enrollments.end_date', 'End Date')}
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('enrollments.mcp', 'Managed Care Plan (MCP)')}
            </label>
            <input
              type="text"
              list="mcp-options"
              value={(formData as any).mcp || ''}
              onChange={(e) => setFormData({ ...formData, mcp: e.target.value } as any)}
              placeholder={t('enrollments.mcp_placeholder', 'MCP this enrollment is aligned to')}
              className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            />
            <datalist id="mcp-options">
              <option value="L.A. Care Health Plan" />
              <option value="Health Net" />
              <option value="Molina Healthcare" />
              <option value="Anthem Blue Cross" />
              <option value="Blue Shield of California Promise" />
              <option value="Kaiser Permanente" />
              <option value="CalOptima" />
              <option value="Inland Empire Health Plan" />
            </datalist>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={formData.eligibility_verified}
                onChange={(e) => setFormData({ ...formData, eligibility_verified: e.target.checked })}
              />
              {t('enrollments.eligibility_verified', 'Eligibility Verified')}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={formData.consent_obtained}
                onChange={(e) => setFormData({ ...formData, consent_obtained: e.target.checked })}
              />
              {t('enrollments.consent_obtained', 'Consent Obtained')}
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('enrollments.notes', 'Notes')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder={t('enrollments.add_any_notes_about_this', 'Add any notes about this enrollment...')}
              rows={3}
              className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={onBack} disabled={enrollmentLoading}>
              {t('enrollments.cancel', 'Cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={enrollmentLoading}>
              {enrollmentLoading ? t('enrollments.creating', 'Creating...') : t('enrollments.create_enrollment', 'Create Enrollment')}
            </Button>
          </div>
        </form>
      </Card>

      {episodeDetection && (
        <NewCaseDetectionDialog
          open={showDetectionDialog}
          onOpenChange={(open) => {
            setShowDetectionDialog(open);
            if (!open) onBack();
          }}
          detection={episodeDetection}
          memberId={formData.member_id}
          programId={formData.program_id}
          cboId={parseInt(localStorage.getItem('active_cbo_id') || '0', 10)}
          onCaseCreated={() => onBack()}
          onSkipped={() => onBack()}
        />
      )}
    </div>
  );
}
